import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../context/AuthContext';
import './Login.css';

function Login() {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedPath = location.state?.from;
  const redirectTo = (
    typeof requestedPath === 'string'
    && requestedPath.startsWith('/')
    && !requestedPath.startsWith('//')
  ) ? requestedPath : '/dashboard';

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError('');
  };

  const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  // Test key (6LeIxAcT...) is Google's public test key — treat as disabled
  const TEST_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  const captchaEnabled = RECAPTCHA_KEY && RECAPTCHA_KEY !== TEST_KEY;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!userId || !password) {
      setError('Please enter both User ID and Password');
      return;
    }

    if (captchaEnabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const result = await login(userId, password, recaptchaToken);
      if (result.success) {
        if (result.mustChangePassword) {
          let message;
          if (result.passwordNonCompliant) {
            message = 'Your password does not meet current security requirements. Please update your password to continue.';
          } else if (result.passwordExpired) {
            message = 'Your password has expired. For security, passwords must be changed annually. Please update your password to continue.';
          } else {
            message = 'Your administrator has reset your password. You must change it before accessing the system.';
          }
          
          navigate('/change-password', {
            state: {
              forced: true,
              message: message,
              from: redirectTo,
            }
          });
        } else {
          navigate(redirectTo, { replace: true });
        }
      } else {
        setError(result.message || 'Invalid credentials');
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-logo-container">
        <img src="/logo.png" alt="Hanwha Power" className="login-logo-img" />
      </div>

      <div className="login-header-bar">Warehouse Management System</div>

      <div className="login-content">
        <div className="login-box">
          <p className="login-instruction">Please enter your UserID and Password below:</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="userId">User ID</label>
              <input
                type="text"
                id="userId"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group recaptcha-container">
              {captchaEnabled && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_KEY}
                  onChange={handleRecaptchaChange}
                />
              )}
            </div>

            <div className="form-group">
              <button type="submit" className="login-button" disabled={loading || (captchaEnabled && !recaptchaToken)}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>

            <div className="forgot-password-link">
              <Link to="/forgot-password">Forgot your password?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
